import httpWrapper from '../HttpWrapper'
import { GerPersonByIdRes } from './models/get/SearchPersonsRes'
import { GetPersonByIdsRes } from './models/getByIds/GetPersonByIdsRes'
import { CandidatesBooleanSearchRes } from './models/getCandidatesBooleanSearch/CandidatesBooleanSearch'
import { GetPersonsLiteReq } from './models/getLite/GetPersonsLiteReq'
import { GetPersonsLiteRes } from './models/getLite/GetPersonsLiteRes'
import { SearchPersonsRes } from './models/search/SearchPersonsRes'
import {
    CsPersonWorkHistoryFilter,
    SearchCsPersonsRes,
} from './models/searchCs/SearchPersonsCsDtos'
import { UpdateRelationshipScoreReq } from './models/updateRelationshipScore/UpdateRelationshipScoreReq'
import {
    ImportCsPersonReq,
    ImportCsPersonRes,
} from './models/importCs/ImportCsPersonDtos'

export type LinkNewUserToTenantStatus =
    | 'completed'
    | 'needs_work_history_selection'

export type LinkedInWorkHistoryOption = {
    workHistoryId: string
    companyId?: string
    companyName?: string
    jobTitle?: string
}

export type LinkNewUserToTenantRes =
    | {
          status: Extract<LinkNewUserToTenantStatus, 'completed'>
          tenantId?: string
      }
    | {
          status: Extract<
              LinkNewUserToTenantStatus,
              'needs_work_history_selection'
          >
          workHistoryOptions: LinkedInWorkHistoryOption[]
      }

export type LinkNewUserToTenantReq = {
    linkedinUrl: string
    personId?: string
    workHistoryId?: string
}

export type PersonNetworkImportStatusSummaryDto = {
    status: 'EXCLUDED' | 'IN_PROGRESS' | 'COMPLETE' | 'FAILED' | 'CACHE_INVALID'
    hasStatusRow: boolean
    latestLogStatus?:
        | 'DISPATCH_PENDING'
        | 'QUEUED'
        | 'IMPORT_IN_PROGRESS'
        | 'IMPORT_COMPLETE'
        | 'NETWORK_GEN_IN_PROGRESS'
        | 'COMPLETE'
        | 'FAILED'
    errMsg?: string | null
    statusId?: string
}

type GetNetworkImportStatusResDto = {
    statuses: Record<string, PersonNetworkImportStatusSummaryDto>
}

type GetNetworkImportStatusReqDto = {
    personIds: string[]
}

type GetCsNetworkConnectionCountsResDto = {
    counts: Record<string, number>
}

export type FindRandomKitTestPersonReq = {
    minWorkHistoryCount?: number
    minNetworkConnectionCount?: number
    timeoutMs?: number
}

export type FindRandomKitTestPersonRes = {
    personId: string
    linkedInUrl: string
    workHistoryCount: number
    networkConnectionCount: number
}

export type PersonNetworkImportStatusValue =
    PersonNetworkImportStatusSummaryDto['status']

export type PersonNetworkImportLogStatusValue =
    | 'DISPATCH_PENDING'
    | 'QUEUED'
    | 'STARTED'
    | 'IMPORT_IN_PROGRESS'
    | 'IMPORT_COMPLETE'
    | 'NETWORK_GEN_IN_PROGRESS'
    | 'COMPLETE'
    | 'FAILED'

export type PersonNetworkImportQueueLogDto = {
    id: string
    status: PersonNetworkImportLogStatusValue
    errMsg: string | null
    sqsMsgId: string | null
    sqsReceiptHandle: string | null
    sqsMsgPayload: string | null
    sfnExeName: string | null
    sfnExecutionUrl: string | null
    createdAt: string
    updatedAt: string
    queuedAt: string | null
    startedAt: string | null
    finishedAt: string | null
    timeWaitingToQueueMs: number | null
    timeInQueueMs: number | null
    timeInProcessingMs: number | null
    timeTotalMs: number | null
}

export type PersonNetworkImportQueuePersonDto = {
    id: string
    firstName?: string
    middleName?: string
    lastName?: string
    fullName?: string
    headline?: string
    linkedInProfilePicUrl?: string
}

export type PersonNetworkImportQueueItemDto = {
    id: string
    personId: string
    status: PersonNetworkImportStatusValue
    errMsg: string | null
    createdAt: string
    updatedAt: string
    createdBy: string
    createdByEmail: string | null
    person?: PersonNetworkImportQueuePersonDto
    logs: PersonNetworkImportQueueLogDto[]
}

export type GetPersonNetworkImportQueueRes = {
    items: PersonNetworkImportQueueItemDto[]
    pageNumber: number
    perPage: number
    total: number
    hasMore: boolean
}

export type GetPersonNetworkImportQueueReq = {
    pageNumber?: number
    perPage?: number
    statuses?: PersonNetworkImportStatusValue[]
    personId?: string
}

export type PurgePersonNetworkImportQueueReq = {
    confirmation: string
}

export type PurgePersonNetworkImportQueueRes = {
    message: string
}

export type ManualFailPersonNetworkImportLogReq = {
    statusId: string
    logId: string
    sqsReceiptHandle?: string
    setStatusToFailed: boolean
}

export type ManualFailPersonNetworkImportLogRes = {
    message: string
}

export type ImportPersonNetworkReq = {
    personId: string
    allowCompleteToBeReprocessed?: boolean
}

export type ImportPersonNetworkRes = {
    queuedPersonIds: string[]
    message?: string
}

export type GetCsNetworkConnectionCountsReq = {
    personIds: string[]
}

class PersonApi {
    private static instance: PersonApi

    public constructor() {
        if (PersonApi.instance) {
            return PersonApi.instance
        }

        PersonApi.instance = this
    }

    public async searchPersons(searchTerm: string): Promise<SearchPersonsRes> {
        const queryParams = new URLSearchParams({
            q: searchTerm,
        })

        return await httpWrapper.get<SearchPersonsRes>(
            '/person/search',
            queryParams,
        )
    }

    public async searchPersonsCs(
        searchTerm: string,
        options: {
            pageNumber?: number
            perPage?: number
            searchMode?:
                | 'full_name'
                | 'first_last_name'
                | 'last_name'
                | 'li_url'
            workHistoryFilter?: CsPersonWorkHistoryFilter
        } = {},
    ): Promise<SearchCsPersonsRes> {
        const pageNumber = options.pageNumber ?? 1
        const perPage = options.perPage ?? 50
        const queryParams = new URLSearchParams({
            q: searchTerm,
            pageNumber: String(pageNumber),
            perPage: String(perPage),
        })

        if (options.searchMode) {
            queryParams.set('searchMode', options.searchMode)
        }

        if (options.workHistoryFilter && options.workHistoryFilter !== 'all') {
            queryParams.set('workHistoryFilter', options.workHistoryFilter)
        }

        return await httpWrapper.get<SearchCsPersonsRes>(
            '/person/search-cs-persons-admin',
            queryParams,
        )
    }

    public async searchPersonsCsByFullName(
        searchTerm: string,
        options: {
            pageNumber?: number
            perPage?: number

            priorityCompanyIds?: string[]
            workHistoryFilter?: CsPersonWorkHistoryFilter
        } = {},
    ): Promise<SearchCsPersonsRes> {
        const pageNumber = options.pageNumber ?? 1
        const perPage = options.perPage ?? 50
        const queryParams = new URLSearchParams({
            q: searchTerm,
            pageNumber: String(pageNumber),
            perPage: String(perPage),
        })

        if (options.priorityCompanyIds?.length) {
            for (const companyId of options.priorityCompanyIds) {
                if (companyId?.trim()) {
                    queryParams.append('priorityCompanyIds', companyId.trim())
                }
            }
        }

        if (options.workHistoryFilter && options.workHistoryFilter !== 'all') {
            queryParams.set('workHistoryFilter', options.workHistoryFilter)
        }

        return await httpWrapper.get<SearchCsPersonsRes>(
            '/person/search-cs-by-full-name',
            queryParams,
        )
    }

    public async searchPersonsCsById(
        searchTerm: string,
        pageNumber: number = 1,
        perPage: number = 50,
        options: { workHistoryFilter?: CsPersonWorkHistoryFilter } = {},
    ): Promise<SearchCsPersonsRes> {
        const queryParams = new URLSearchParams({
            q: searchTerm,
            pageNumber: String(pageNumber),
            perPage: String(perPage),
        })

        if (options.workHistoryFilter && options.workHistoryFilter !== 'all') {
            queryParams.set('workHistoryFilter', options.workHistoryFilter)
        }

        return await httpWrapper.get<SearchCsPersonsRes>(
            '/person/search-cs-by-id-admin',
            queryParams,
        )
    }

    public async searchPersonsCsByLinkedInUrl(
        searchTerm: string,
        pageNumber: number = 1,
        perPage: number = 50,
        options: {
            priorityCompanyIds?: string[]
            workHistoryFilter?: CsPersonWorkHistoryFilter
        } = {},
    ): Promise<SearchCsPersonsRes> {
        const queryParams = new URLSearchParams({
            q: searchTerm,
            pageNumber: String(pageNumber),
            perPage: String(perPage),
        })

        if (options.priorityCompanyIds?.length) {
            for (const companyId of options.priorityCompanyIds) {
                if (companyId?.trim()) {
                    queryParams.append('priorityCompanyIds', companyId.trim())
                }
            }
        }

        if (options.workHistoryFilter && options.workHistoryFilter !== 'all') {
            queryParams.set('workHistoryFilter', options.workHistoryFilter)
        }

        return await httpWrapper.get<SearchCsPersonsRes>(
            '/person/search-cs-by-linkedin-url',
            queryParams,
        )
    }

    public async listPersonsCs(
        pageNumber: number = 1,
        perPage: number = 100,
        options: { workHistoryFilter?: CsPersonWorkHistoryFilter } = {},
    ): Promise<SearchCsPersonsRes> {
        const queryParams = new URLSearchParams({
            pageNumber: String(pageNumber),
            perPage: String(perPage),
        })

        if (options.workHistoryFilter && options.workHistoryFilter !== 'all') {
            queryParams.set('workHistoryFilter', options.workHistoryFilter)
        }

        return await httpWrapper.get<SearchCsPersonsRes>(
            '/person/list-cs-admin',
            queryParams,
        )
    }

    public async getNetworkImportStatuses(
        personIds: string[],
    ): Promise<Record<string, PersonNetworkImportStatusSummaryDto>> {
        const uniquePersonIds = Array.from(
            new Set(
                personIds
                    .map((personId) => personId?.trim())
                    .filter(Boolean) as string[],
            ),
        )
        if (!uniquePersonIds.length) return {}
        const res = await httpWrapper.post<
            GetNetworkImportStatusReqDto,
            GetNetworkImportStatusResDto
        >('/person/get-network-import-status', { personIds: uniquePersonIds })
        return res.statuses
    }

    public async getCsNetworkConnectionCounts(
        personIds: string[],
    ): Promise<Record<string, number>> {
        if (!personIds.length) return {}
        const res = await httpWrapper.post<
            GetCsNetworkConnectionCountsReq,
            GetCsNetworkConnectionCountsResDto
        >('/person/get-cs-network-connection-counts-admin', { personIds })
        return res.counts
    }

    public async findRandomKitTestPerson(
        req: FindRandomKitTestPersonReq = {},
    ): Promise<FindRandomKitTestPersonRes> {
        return await httpWrapper.post<
            FindRandomKitTestPersonReq,
            FindRandomKitTestPersonRes
        >('/person/find-random-kit-test-person-admin', req)
    }

    public async getPersonNetworkImportQueue(
        params: GetPersonNetworkImportQueueReq = {},
    ): Promise<GetPersonNetworkImportQueueRes> {
        const queryParams = new URLSearchParams()

        if (params.pageNumber !== undefined) {
            queryParams.set('pageNumber', String(params.pageNumber))
        }

        if (params.perPage !== undefined) {
            queryParams.set('perPage', String(params.perPage))
        }

        if (params.statuses?.length) {
            params.statuses.forEach((status) =>
                queryParams.append('statuses', status),
            )
        }

        if (params.personId) {
            queryParams.set('personId', params.personId)
        }

        return await httpWrapper.get<GetPersonNetworkImportQueueRes>(
            '/person/network-import-queue-admin',
            queryParams,
        )
    }

    public async purgePersonNetworkImportQueue(
        req: PurgePersonNetworkImportQueueReq,
    ): Promise<PurgePersonNetworkImportQueueRes> {
        return await httpWrapper.post<
            PurgePersonNetworkImportQueueReq,
            PurgePersonNetworkImportQueueRes
        >('/person/network-import-queue-admin/purge', req)
    }

    public async manualFailPersonNetworkImportLog(
        req: ManualFailPersonNetworkImportLogReq,
    ): Promise<ManualFailPersonNetworkImportLogRes> {
        return await httpWrapper.post<
            ManualFailPersonNetworkImportLogReq,
            ManualFailPersonNetworkImportLogRes
        >('/person/network-import-queue/manual-fail', req)
    }

    public async importPersonNetwork(
        req: ImportPersonNetworkReq,
    ): Promise<ImportPersonNetworkRes> {
        return await httpWrapper.post<
            ImportPersonNetworkReq,
            ImportPersonNetworkRes
        >('/person/network-import-queue/import', req)
    }

    public async getNumberOfPersonNodes(): Promise<number> {
        return await httpWrapper.get('/person/count-admin')
    }

    public async getPersonsLite(
        data: GetPersonsLiteReq,
    ): Promise<GetPersonsLiteRes> {
        if (data.personIds.length === 0) {
            return { persons: [] }
        }

        return await httpWrapper.post('/person/get-lite', data)
    }

    public async getPersonById(personId: string): Promise<GerPersonByIdRes> {
        const queryParams = new URLSearchParams({ personId })
        return await httpWrapper.get<GerPersonByIdRes>(
            '/person/get-by-id',
            queryParams,
        )
    }

    public async getPersonsById(
        personIds: string[],
    ): Promise<GetPersonByIdsRes> {
        const queryParams = new URLSearchParams()
        personIds.forEach((id) => queryParams.append('personIds', id))
        return await httpWrapper.get<GetPersonByIdsRes>(
            '/person/get-by-ids',
            queryParams,
        )
    }

    public async getCandidatesBooleanSearch(
        searchTerm: string,
        ignoreRetiredPeople: boolean = false,
        excludeCurrentColleagues: boolean = false,
        ids: string[] = [],
    ): Promise<CandidatesBooleanSearchRes> {
        const queryParams = new URLSearchParams({
            query: searchTerm,
            ignoreRetiredPeople: ignoreRetiredPeople.toString(),
            excludeCurrentColleagues: excludeCurrentColleagues.toString(),
            ids: ids.toString(),
        })

        return await httpWrapper.get<CandidatesBooleanSearchRes>(
            '/person/get-persons-boolean-search',
            queryParams,
        )
    }

    public async updateRelationshipScore(
        data: UpdateRelationshipScoreReq,
    ): Promise<void> {
        await httpWrapper.post('/person/update-relationship-score', data)
    }

    public async importPersonFromCs(
        req: ImportCsPersonReq,
    ): Promise<ImportCsPersonRes> {
        return await httpWrapper.post('/person/import-cs-person-admin', req)
    }

    public async importPersonAndLinkToUser(
        req: ImportCsPersonReq,
    ): Promise<ImportCsPersonRes> {
        return await httpWrapper.post(
            '/person/import-cs-person-and-link-to-user',
            req,
        )
    }

    public async linkNewUserToTenant(
        req: LinkNewUserToTenantReq,
    ): Promise<LinkNewUserToTenantRes> {
        return await httpWrapper.post('/person/link-user-to-tenant', req)
    }
}

const personApi = new PersonApi()
export default personApi
